// src/pages/Blog.jsx
import React, { useEffect, useMemo, useRef, useState } from "react";
import "./Blog.css";
import { blogs } from "../../data.js";  // <-- ensure this path is correct
import Button from "../../components/button/Button";
import { useTranslation } from "react-i18next";

/* ---- tiny reveal-on-scroll hook ---- */
function useReveal(options = { threshold: 0.18, rootMargin: "0px 0px -10% 0px" }) {
  const ref = useRef(null);
  const [inView, setInView] = useState(false);

  useEffect(() => {
    if (!ref.current || inView) return;
    if (!("IntersectionObserver" in window)) {
      setInView(true);
      return;
    }
    const io = new IntersectionObserver(([entry]) => {
      if (entry.isIntersecting) {
        setInView(true);
        io.disconnect();
      }
    }, options);
    io.observe(ref.current);
    return () => io.disconnect();
  }, [inView, options]);

  return [ref, inView];
}

/* rough read-time estimator if not provided (≈220 wpm) */
function estimateReadTime(text = "") {
  const words = (text.trim().match(/\S+/g) || []).length;
  return Math.max(1, Math.round(words / 220));
}

export default function Blog() {
  const { t } = useTranslation();

  // helper: translate with fallback
  const txt = (key, fallback) =>
    key ? t(key, { defaultValue: fallback }) : fallback;

  // Sort newest first if dates are present
  const posts = useMemo(() => {
    if (!blogs) return [];
    const arr = [...blogs];
    // parse date robustly (ISO recommended)
    return arr.sort((a, b) => {
      const da = a.date ? new Date(a.date) : 0;
      const db = b.date ? new Date(b.date) : 0;
      return db - da;
    });
  }, []);

  // Fallback: first available image across posts
  const fallbackImage = useMemo(() => {
    const withImg = posts.find((p) => p?.image);
    return withImg?.image || posts[0]?.image || "https://security-backend-1nbc.onrender.com/api/placeholder/400/300";
  }, [posts]);

  // Use each post's own image; do NOT overwrite with the fallback
  const fourPosts = useMemo(() => {
    if (!posts.length) return [];
    return Array.from({ length: Math.min(4, posts.length) }, (_, i) => posts[i]);
  }, [posts]);

  // reveals
  const [headRef, headIn] = useReveal();
  const [rowRef, rowIn] = useReveal();

  // newsletter form state
  const [email, setEmail] = useState("");
  const [status, setStatus] = useState("idle"); // idle | loading | success | error
  const [err, setErr] = useState("");

  async function onSubmit(e) {
    e.preventDefault();
    setErr("");
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      setErr(t("blogs.newsletter.error.invalidEmail", { defaultValue: "Please enter a valid email." }));
      return;
    }
    try {
      setStatus("loading");
      const res = await fetch("/api/newsletter/subscribe", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email })
      });
      if (!res.ok) throw new Error("Request failed");
      setStatus("success");
      setEmail("");
    } catch {
      setStatus("error");
      setErr(t("blogs.newsletter.error.generic", { defaultValue: "Something went wrong. Try again." }));
    }
  }

  return (
    <section id="blog" aria-labelledby="blog-heading">
      <div className="blog__container">
        {/* Header: left = jumbo headline, right = newsletter form */}
        <div
          ref={headRef}
          className={`blog__header reveal reveal--up ${headIn ? "in" : ""}`}
        >
          <div className="blog__headerLeft reveal-item" style={{ transitionDelay: "80ms" }}>
            <p className="blog__eyebrow">
              {t("blogs.eyebrow", { defaultValue: "Trends & insights" })}
            </p>
            <h2 id="blog-heading" className="blog__headline">
              {t("blogs.heading", { defaultValue: "We’re raising the bar for the security industry" })}
            </h2>
          </div>

          <div className="blog__headerRight reveal-item" style={{ transitionDelay: "160ms" }}>
            <p className="blog__intro">
              {t("blogs.newsletter.copy", {
                defaultValue: "Get practical insights, security tips, and updates in your inbox. No spam—unsubscribe anytime."
              })}
            </p>

            {/* Newsletter form (aligned row; uses Button component) */}
            <form className="headerForm" onSubmit={onSubmit}>
              <label className="sr-only" htmlFor="blog-newsletter-email">
                {t("blogs.newsletter.emailLabel", { defaultValue: "Email" })}
              </label>
              <input
                id="blog-newsletter-email"
                type="email"
                placeholder={t("blogs.newsletter.placeholder", { defaultValue: "you@example.com" })}
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                aria-invalid={err ? "true" : "false"}
              />
              <Button
                type="submit"
                className="headerForm__btn"
                outline="#1F2366"
                color="linear-gradient(135deg, rgba(31,35,62,1) 0%, #3244ba 100%)"
              >
                {status === "loading"
                  ? t("blogs.newsletter.subscribing", { defaultValue: "Subscribing..." })
                  : t("blogs.newsletter.submit", { defaultValue: "Subscribe" })}
              </Button>
            </form>

            {err && <p className="newsletter__error" role="alert">{err}</p>}
            {status === "success" && (
              <p className="newsletter__success" role="status">
                {t("blogs.newsletter.success", { defaultValue: "Thanks! Please check your inbox to confirm." })}
              </p>
            )}
          </div>
        </div>

        {/* Posts row */}
        <div
          ref={rowRef}
          className={`posts__row reveal reveal--up ${rowIn ? "in" : ""}`}
          role="list"
          aria-label={t("blogs.latestAria", { defaultValue: "Latest articles" })}
        >
          {fourPosts.length ? (
            fourPosts.map((post, i) => {
              const link = post.linkTo || post.url || (post.slug ? `/blog/${post.slug}` : "#");

              const dtObj = post.date ? new Date(post.date) : null;
              const displayDate = dtObj
                ? dtObj.toLocaleDateString(undefined, {
                    year: "numeric",
                    month: "long",
                    day: "numeric"
                  })
                : null;

              const read = post.readTime || estimateReadTime(post.content || post.excerpt || "");
              const authorValue = txt(post.tAuthor, post.author);
              const author = authorValue ? t("blogs.byAuthor", { defaultValue: "By {{name}}", name: authorValue }) : null;
              const isFeatured = i === 0; // First post is featured

              const imgSrc = post.image || fallbackImage;
              const title = txt(post.tTitle, post.title);
              const excerpt = txt(post.tExcerpt, post.excerpt);
              const imgAlt = post.imageAlt || title || t("blogs.imageAlt", { defaultValue: "Blog image" });

              return (
                <article
                  key={`${post.slug || post.title || "post"}-${i}`}
                  className={`postCard ${isFeatured ? "postCard--featured" : ""} reveal-item`}
                  style={{ transitionDelay: `${100 + i * 80}ms` }}
                  role="listitem"
                >
                  {isFeatured && (
                    <div className="postCard__featuredBadge">
                      {t("blogs.featured", { defaultValue: "Featured" })}
                    </div>
                  )}

                  <a href={link} className="postCard__imageLink" aria-label={t("blogs.readMoreAria", { defaultValue: "Read more: {{title}}", title })}>
                    {imgSrc && (
                      <img
                        src={imgSrc}
                        alt={imgAlt}
                        className="postCard__image"
                        loading="lazy"
                      />
                    )}
                  </a>

                  <h3 className="postCard__title">
                    <a href={link}>{title}</a>
                  </h3>

                  {isFeatured && excerpt && (
                    <p className="postCard__excerpt">{excerpt}</p>
                  )}

                  <div className="postCard__meta">
                    {author && <span className="postCard__by">{author}</span>}
                    {(author && (displayDate || read)) && <span className="postCard__dot"> • </span>}
                    {displayDate && <time className="postCard__date" dateTime={post.date}>{displayDate}</time>}
                    {(displayDate && read) && <span className="postCard__sep"> | </span>}
                    {read && (
                      <span className="postCard__read">
                        {t("blogs.readTime", { defaultValue: "{{min}} min read", min: read })}
                      </span>
                    )}
                  </div>
                </article>
              );
            })
          ) : (
            <div className="blog__empty">{t("blogs.empty", { defaultValue: "No posts yet." })}</div>
          )}
        </div>
      </div>
    </section>
  );
}
